import ChatIcon from '@mui/icons-material/ChatOutlined';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { FC, KeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { memo, useMemo, useRef, useState } from 'react';

import { RepairOrderStatusE, WipPriorityE, WipStatusE } from 'declarations/enums/wip';
import type { OemFamilyClaimStatusesI, WipRecordI } from 'declarations/interfaces/wip';
import { mapAgeToColor } from 'pages/TicketsPage/helpers';

import { type WipEditableRowT, validateWipRow } from './validateWipRow';
import type { WipItemCellI } from './WIPItemsTableConfiguration';

/**
 * Data entry dealer option for the WIP new-row dropdown. Includes oemFamilyId so the row can
 * resolve the matching claim status catalog when a dealer is picked (mirrors WarrQueue's
 * MappedRoofTopAccountDto.oemFamilyId behavior).
 */
export interface WipDataEntryDealerI {
  id: string;
  name: string;
  oemFamilyId: string;
}

const DEALER_LAST_RESPONSE_FROM = 2;

const formatDayjsToIsoDateOnly = (date: Dayjs): string => date.format('YYYY-MM-DD');

const getDerivedWipStatus = (
  assigneeId: string | null | undefined,
  previousWipStatus: WipStatusE | undefined
): WipStatusE => {
  if (previousWipStatus === WipStatusE.Completed) return WipStatusE.Completed;
  if (assigneeId != null && String(assigneeId).trim() !== '') return WipStatusE.Working;
  return WipStatusE.Pending;
};

export interface WIPItemRowPropsI {
  row?: WipRecordI;
  visibleColumns: WipItemCellI[];
  lastEditableColumn: WipItemCellI | undefined;
  dealers: WipDataEntryDealerI[];
  oemFamilyClaimStatuses: OemFamilyClaimStatusesI[];
  onSave: (row: WipEditableRowT) => void;
  onValidationError?: (message: string) => void;
  isNewRow?: boolean;
  /** Whether this row's checkbox is currently selected. */
  isItemSelected?: boolean;
  /** Fired when the user toggles this row's checkbox. */
  onToggleSelected?: (wipId: string) => void;
  /**
   * Pre-selects the dealer dropdown for a new entry row. The caller resolves this from the
   * WIP dealer card (selected rooftop, or the single-dealer auto-select case) so the user
   * doesn't have to pick when there's an obvious default.
   */
  defaultRoofTopId?: string | null;
}

const CHECKBOX_COLUMN_WIDTH = 45;
const CHECKBOX_SIZE = 42;
const CHECKBOX_ICON_FONT_SIZE = 24;

const WIPItemRowComponent: FC<WIPItemRowPropsI> = ({
  row: rowProp,
  visibleColumns,
  lastEditableColumn,
  dealers,
  oemFamilyClaimStatuses,
  onSave,
  onValidationError,
  isNewRow: isNewRowProp,
  isItemSelected = false,
  onToggleSelected,
  defaultRoofTopId,
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const isNewRow = isNewRowProp ?? rowProp == null;
  const row = useMemo<Partial<WipRecordI>>(
    (): Partial<WipRecordI> =>
      rowProp ?? ({ wipStatus: WipStatusE.Pending } as Partial<WipRecordI>),
    [rowProp]
  );

  // Seed defaults that mirror the WarrQueue WIP data entry row: Priority defaults to Medium and
  // Backlog defaults to No until the user changes them. When the WIP dealer card has a single
  // dealer (or the user has explicitly selected one), pre-select that dealer so the operator
  // can type just the RO Number and hit Enter to save.
  const initialEditedData = useMemo<WipEditableRowT>((): WipEditableRowT => {
    if (!(isNewRowProp ?? rowProp == null)) return {};
    const matchedDealer =
      defaultRoofTopId != null && defaultRoofTopId !== ''
        ? dealers.find((d): boolean => d.id === defaultRoofTopId)
        : undefined;
    return {
      priority: WipPriorityE.Medium,
      backlog: false,
      ...(matchedDealer ? { roofTopId: matchedDealer.id, roofTopName: matchedDealer.name } : {}),
    };
  }, [isNewRowProp, rowProp, defaultRoofTopId, dealers]);
  const [editedData, setEditedData] = useState<WipEditableRowT>(initialEditedData);
  const [validationErrors, setValidationErrors] = useState<Set<keyof WipRecordI>>(
    (): Set<keyof WipRecordI> => new Set()
  );

  const dataRef = useRef({ editedData, row });
  dataRef.current = { editedData, row };

  const allowedClaimStatuses = useMemo((): string[] => {
    let oemFamilyId: string | undefined;
    if (isNewRow) {
      const roofTopId = editedData.roofTopId ?? row.roofTopId ?? '';
      oemFamilyId = dealers.find((d): boolean => d.id === roofTopId)?.oemFamilyId;
    } else {
      oemFamilyId = row.oemFamilyId ?? editedData.oemFamilyId;
    }
    if (!oemFamilyId) return [];
    const match = oemFamilyClaimStatuses.find((o): boolean => o.oemFamilyId === oemFamilyId);
    return match?.claimStatuses ?? [];
  }, [
    isNewRow,
    editedData.roofTopId,
    editedData.oemFamilyId,
    row.roofTopId,
    row.oemFamilyId,
    dealers,
    oemFamilyClaimStatuses,
  ]);

  const clearFieldError = (fieldId: keyof WipRecordI): void => {
    setValidationErrors((prev): Set<keyof WipRecordI> => {
      const next = new Set(prev);
      next.delete(fieldId);
      return next;
    });
  };

  const saveFromRef = (): void => {
    const { editedData: data, row: r } = dataRef.current;
    const payload: WipEditableRowT = { ...r, ...data };
    onSave(payload);
    setEditedData(initialEditedData);
    setValidationErrors(new Set());
  };

  /**
   * Shared validate-then-save routine used by both the Tab-from-last-field auto-commit and the
   * Enter-on-RO Number quick-add path. Defers a tick so React flushes the latest input edits
   * before we read `dataRef.current`.
   */
  const attemptSave = (): void => {
    setTimeout((): void => {
      const { editedData: data, row: r } = dataRef.current;
      const payload: WipEditableRowT = { ...r, ...data };
      const { valid, failedFieldIds, message } = validateWipRow(
        payload,
        visibleColumns,
        isNewRow,
        allowedClaimStatuses
      );
      if (!valid) {
        setValidationErrors(new Set(failedFieldIds));
        onValidationError?.(message);
        return;
      }
      setValidationErrors(new Set());
      saveFromRef();
    }, 0);
  };

  const createTabSaveHandler = (
    isLastEditable: boolean
  ): ((e: KeyboardEvent<HTMLElement>) => void) | undefined =>
    isLastEditable
      ? (e: KeyboardEvent<HTMLElement>): void => {
          if (e.key !== 'Tab') return;
          attemptSave();
        }
      : undefined;

  const renderField = (column: WipItemCellI, isLastEditable: boolean): ReactNode => {
    const onTabSave = createTabSaveHandler(isLastEditable);
    const hasError = validationErrors.has(column.id);

    if (column.id === 'repairOrderNumber') {
      if (!isNewRow) return row.repairOrderNumber ?? '';
      return (
        <TextField
          size="small"
          value={editedData.repairOrderNumber ?? row.repairOrderNumber ?? ''}
          onChange={(e): void => {
            clearFieldError('repairOrderNumber');
            setEditedData({ ...editedData, repairOrderNumber: e.target.value });
          }}
          onKeyDownCapture={onTabSave}
          placeholder="RO"
          error={hasError}
        />
      );
    }

    if (column.id === 'roofTopName') {
      if (!isNewRow) return row.roofTopName ?? '';

      const currentRoofTopId = editedData.roofTopId || row.roofTopId || '';
      const hasCurrentInList = dealers.some((r): boolean => r.id === currentRoofTopId);
      const displayOptions: WipDataEntryDealerI[] =
        currentRoofTopId && !hasCurrentInList
          ? [
              {
                id: currentRoofTopId,
                name: (row.roofTopName || currentRoofTopId).trim() || currentRoofTopId,
                oemFamilyId: row.oemFamilyId ?? '',
              },
              ...dealers,
            ]
          : dealers;

      return (
        <FormControl size="small" fullWidth error={hasError}>
          <Select
            value={currentRoofTopId}
            onChange={(e): void => {
              clearFieldError('roofTopName');
              const value = e.target.value as string;
              setEditedData({
                ...editedData,
                roofTopId: value,
                roofTopName: displayOptions.find((r): boolean => r.id === value)?.name ?? '',
                oemClaimStatus: '',
              });
            }}
            onKeyDownCapture={onTabSave}
            displayEmpty
            renderValue={(v): string => {
              if (!v) return 'Select dealer';
              return displayOptions.find((r): boolean => r.id === v)?.name ?? String(v);
            }}
          >
            {displayOptions.length === 0 ? (
              <MenuItem disabled value="">
                No dealerships available
              </MenuItem>
            ) : (
              displayOptions.map(
                (dealer, idx): JSX.Element => (
                  <MenuItem key={`dealer-${idx}-${dealer.id}`} value={dealer.id}>
                    {dealer.name || dealer.id}
                  </MenuItem>
                )
              )
            )}
          </Select>
        </FormControl>
      );
    }

    if (column.id === 'age') {
      if (isNewRow) return '';
      return (
        <Typography variant="body2" color={mapAgeToColor(row.age ?? 0) as 'text.primary'}>
          {row.age}
        </Typography>
      );
    }

    if (column.id === 'openRepairOrderDate') {
      const getDateValue = (): Dayjs | null => {
        const value = editedData.openRepairOrderDate ?? row.openRepairOrderDate;
        return value ? dayjs(value) : null;
      };
      return (
        <DatePicker
          value={getDateValue()}
          onChange={(date: Dayjs | null): void => {
            clearFieldError('openRepairOrderDate');
            setEditedData({
              ...editedData,
              openRepairOrderDate: date ? formatDayjsToIsoDateOnly(date) : null,
            });
          }}
          slotProps={{
            textField: {
              size: 'small',
              error: hasError,
              inputProps: { onKeyDownCapture: onTabSave },
            },
          }}
        />
      );
    }

    if (column.id === 'advisorNumber') {
      return (
        <TextField
          size="small"
          value={editedData.advisorNumber ?? row.advisorNumber ?? ''}
          onChange={(e): void => {
            clearFieldError('advisorNumber');
            setEditedData({ ...editedData, advisorNumber: e.target.value });
          }}
          onKeyDownCapture={onTabSave}
          error={hasError}
        />
      );
    }

    if (column.id === 'techNumber') {
      return (
        <TextField
          size="small"
          value={editedData.techNumber ?? row.techNumber ?? ''}
          onChange={(e): void => {
            clearFieldError('techNumber');
            setEditedData({ ...editedData, techNumber: e.target.value });
          }}
          onKeyDownCapture={onTabSave}
          error={hasError}
        />
      );
    }

    if (column.id === 'oemFamilyName') {
      return row.oemFamilyName ?? '';
    }

    if (column.id === 'wipStatus') {
      return editedData.wipStatus ?? row.wipStatus ?? WipStatusE.Pending;
    }

    if (column.id === 'oemClaimStatus') {
      const currentAssigneeId = editedData.assigneeId ?? row.assigneeId ?? null;
      const currentValue = editedData.oemClaimStatus ?? row.oemClaimStatus ?? '';
      let options: string[] = [];
      if (allowedClaimStatuses.length > 0) {
        options =
          currentValue && !allowedClaimStatuses.includes(currentValue)
            ? [currentValue, ...allowedClaimStatuses]
            : allowedClaimStatuses;
      }

      if (options.length === 0) {
        // No OEM family claim status catalog available (Chirp currently returns an empty list)
        // so fall back to a free-text input that lets the user save the row.
        return (
          <TextField
            size="small"
            value={currentValue}
            onChange={(e): void => {
              clearFieldError('oemClaimStatus');
              setEditedData({ ...editedData, oemClaimStatus: e.target.value });
            }}
            onKeyDownCapture={onTabSave}
            placeholder={isNewRow ? 'Claim status' : ''}
            error={hasError}
          />
        );
      }

      return (
        <FormControl size="small" fullWidth error={hasError}>
          <Select
            value={currentValue}
            onChange={(e): void => {
              clearFieldError('oemClaimStatus');
              const value = e.target.value as string;
              const effectiveWipStatus = editedData.wipStatus ?? row.wipStatus;
              setEditedData({
                ...editedData,
                oemClaimStatus: value,
                wipStatus: getDerivedWipStatus(currentAssigneeId, effectiveWipStatus),
              });
            }}
            onKeyDownCapture={onTabSave}
            displayEmpty
            renderValue={(v): string => (v ? String(v) : 'Select status')}
          >
            <MenuItem value="" disabled>
              <em>Select status</em>
            </MenuItem>
            {options.map(
              (status, idx): JSX.Element => (
                <MenuItem key={`claim-status-${idx}-${status}`} value={status}>
                  {status}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      );
    }

    if (column.id === 'priority') {
      const value = editedData.priority ?? row.priority ?? WipPriorityE.Undefined;
      return (
        <FormControl size="small" fullWidth error={hasError}>
          <Select
            value={value}
            onChange={(e): void => {
              clearFieldError('priority');
              setEditedData({ ...editedData, priority: e.target.value as WipPriorityE });
            }}
            onKeyDownCapture={onTabSave}
            displayEmpty
            renderValue={(v): string => (v === WipPriorityE.Undefined ? 'Select' : String(v))}
          >
            <MenuItem value={WipPriorityE.Undefined} disabled>
              <em>Select</em>
            </MenuItem>
            <MenuItem value={WipPriorityE.Low}>{WipPriorityE.Low}</MenuItem>
            <MenuItem value={WipPriorityE.Medium}>{WipPriorityE.Medium}</MenuItem>
            <MenuItem value={WipPriorityE.High}>{WipPriorityE.High}</MenuItem>
            <MenuItem value={WipPriorityE.Critical}>{WipPriorityE.Critical}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.id === 'backlog') {
      const raw = editedData.backlog ?? row.backlog;
      let value: string;
      if (raw === undefined) value = '';
      else if (raw) value = 'Yes';
      else value = 'No';
      return (
        <FormControl size="small" fullWidth error={hasError}>
          <Select
            value={value}
            onChange={(e): void => {
              clearFieldError('backlog');
              const v = e.target.value as string;
              let backlogValue: boolean | undefined;
              if (v === '') backlogValue = undefined;
              else if (v === 'Yes') backlogValue = true;
              else backlogValue = false;
              setEditedData({ ...editedData, backlog: backlogValue });
            }}
            onKeyDownCapture={onTabSave}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em>Select</em>
            </MenuItem>
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.id === 'lineNumber') {
      return (
        <TextField
          size="small"
          value={editedData.lineNumber ?? row.lineNumber ?? ''}
          onChange={(e): void => {
            clearFieldError('lineNumber');
            setEditedData({ ...editedData, lineNumber: e.target.value });
          }}
          onKeyDownCapture={onTabSave}
          error={hasError}
        />
      );
    }

    if (column.id === 'isSublet') {
      const getSubletValue = (): string => {
        if (editedData.isSublet !== undefined) return editedData.isSublet ? 'Yes' : 'No';
        return row.isSublet ? 'Yes' : 'No';
      };
      return (
        <FormControl size="small" fullWidth error={hasError}>
          <Select
            value={getSubletValue()}
            onChange={(e): void =>
              setEditedData({ ...editedData, isSublet: e.target.value === 'Yes' })
            }
            onKeyDownCapture={onTabSave}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.id === 'repairOrderTotal') {
      return (
        <TextField
          size="small"
          type="number"
          value={editedData.repairOrderTotal ?? row.repairOrderTotal ?? ''}
          onChange={(e): void =>
            setEditedData({
              ...editedData,
              repairOrderTotal: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
          onKeyDownCapture={onTabSave}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      );
    }

    if (column.id === 'partsAmountClaimed') {
      return (
        <TextField
          size="small"
          type="number"
          value={editedData.partsAmountClaimed ?? row.partsAmountClaimed ?? ''}
          onChange={(e): void =>
            setEditedData({
              ...editedData,
              partsAmountClaimed: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
          onKeyDownCapture={onTabSave}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      );
    }

    if (column.id === 'partsAmountPaid') {
      return (
        <TextField
          size="small"
          type="number"
          value={editedData.partsAmountPaid ?? row.partsAmountPaid ?? ''}
          onChange={(e): void =>
            setEditedData({
              ...editedData,
              partsAmountPaid: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
          onKeyDownCapture={onTabSave}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      );
    }

    if (column.id === 'laborAmountClaimed') {
      return (
        <TextField
          size="small"
          type="number"
          value={editedData.laborAmountClaimed ?? row.laborAmountClaimed ?? ''}
          onChange={(e): void =>
            setEditedData({
              ...editedData,
              laborAmountClaimed: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
          onKeyDownCapture={onTabSave}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      );
    }

    if (column.id === 'laborAmountPaid') {
      return (
        <TextField
          size="small"
          type="number"
          value={editedData.laborAmountPaid ?? row.laborAmountPaid ?? ''}
          onChange={(e): void =>
            setEditedData({
              ...editedData,
              laborAmountPaid: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
          onKeyDownCapture={onTabSave}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      );
    }

    if (column.id === 'techTime') {
      return (
        <TextField
          size="small"
          type="number"
          value={editedData.techTime ?? row.techTime ?? ''}
          onChange={(e): void =>
            setEditedData({
              ...editedData,
              techTime: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
          onKeyDownCapture={onTabSave}
        />
      );
    }

    if (column.id === 'oemPaidTotal') {
      return (
        <TextField
          size="small"
          type="number"
          value={editedData.oemPaidTotal ?? row.oemPaidTotal ?? ''}
          onChange={(e): void =>
            setEditedData({
              ...editedData,
              oemPaidTotal: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
          onKeyDownCapture={onTabSave}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      );
    }

    if (column.id === 'claimTotal') {
      return (
        <TextField
          size="small"
          type="number"
          value={editedData.claimTotal ?? row.claimTotal ?? ''}
          onChange={(e): void =>
            setEditedData({
              ...editedData,
              claimTotal: e.target.value === '' ? null : parseFloat(e.target.value),
            })
          }
          onKeyDownCapture={onTabSave}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      );
    }

    if (column.id === 'oemPaidDate') {
      const value = editedData.oemPaidDate ?? row.oemPaidDate;
      return (
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(date: Dayjs | null): void => {
            setEditedData({
              ...editedData,
              oemPaidDate: date ? formatDayjsToIsoDateOnly(date) : null,
            });
          }}
          slotProps={{ textField: { size: 'small', inputProps: { onKeyDownCapture: onTabSave } } }}
        />
      );
    }

    if (column.id === 'repairOrderStatusEnum') {
      const value = editedData.repairOrderStatusEnum ?? row.repairOrderStatusEnum ?? '';
      const options: { value: RepairOrderStatusE | ''; label: string }[] = [
        { value: RepairOrderStatusE.OpenProcessing, label: 'Open' },
        { value: RepairOrderStatusE.Completed, label: 'Closed' },
      ];
      return (
        <FormControl size="small" fullWidth>
          <Select<RepairOrderStatusE | ''>
            value={value}
            onChange={(e): void =>
              setEditedData({
                ...editedData,
                repairOrderStatusEnum: (e.target.value as RepairOrderStatusE) || null,
              })
            }
            onKeyDownCapture={onTabSave}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em>Select</em>
            </MenuItem>
            {options.map(
              (opt): JSX.Element => (
                <MenuItem key={opt.label} value={opt.value}>
                  {opt.label}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      );
    }

    if (column.id === 'notes') {
      return (
        <Box sx={{ maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
          <TextField
            size="small"
            fullWidth
            value={editedData.notes ?? row.notes ?? ''}
            onChange={(e): void => setEditedData({ ...editedData, notes: e.target.value })}
            onKeyDownCapture={onTabSave}
          />
        </Box>
      );
    }

    if (column.id === 'submittedDate') {
      const value = editedData.submittedDate ?? row.submittedDate;
      return (
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(date: Dayjs | null): void => {
            clearFieldError('submittedDate');
            setEditedData({
              ...editedData,
              submittedDate: date ? formatDayjsToIsoDateOnly(date) : null,
            });
          }}
          slotProps={{
            textField: {
              size: 'small',
              error: hasError,
              inputProps: { onKeyDownCapture: onTabSave },
            },
          }}
        />
      );
    }

    if (column.id === 'assigneeName') {
      // Chirp does not surface agent assignment; render the existing value (if any) read-only.
      return row.assigneeName ?? '';
    }

    if (column.id === 'hasTickets') {
      if (row.hasTickets && row.ticketLastMessageFrom != null) {
        const fromDealer = row.ticketLastMessageFrom === DEALER_LAST_RESPONSE_FROM;
        return (
          <Tooltip title={`Last Response: ${fromDealer ? 'Dealer' : 'WarrCloud'}`}>
            <ChatIcon sx={{ fontSize: 24 }} color={fromDealer ? 'info' : 'inherit'} />
          </Tooltip>
        );
      }
      return (
        <Tooltip title="No Open Chirp Ticket">
          <Box width={24} height={24} display="flex" alignItems="center" justifyContent="center" />
        </Tooltip>
      );
    }

    const fallback = row[column.id];
    if (fallback == null) return '';
    return String(fallback);
  };

  const handleCheckboxClick = (e: ReactMouseEvent<HTMLButtonElement>): void => {
    if (isNewRow || !onToggleSelected || !row.wipId) return;
    e.stopPropagation();
    onToggleSelected(row.wipId);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TableRow
        hover
        onMouseEnter={(): void => setIsHovered(true)}
        onMouseLeave={(): void => setIsHovered(false)}
        selected={isItemSelected}
        sx={{
          '& .MuiInputBase-root': {
            fontSize: (theme): string => theme.typography.body2.fontSize as string,
          },
          '& .MuiOutlinedInput-input': {
            paddingLeft: (theme): string => theme.spacing(1.5),
            paddingRight: (theme): string => theme.spacing(1.5),
          },
          '& .MuiOutlinedInput-root.MuiInputBase-adornedStart .MuiOutlinedInput-input': {
            paddingLeft: 0,
          },
        }}
      >
        <TableCell
          padding="checkbox"
          sx={{
            minWidth: CHECKBOX_COLUMN_WIDTH,
            width: CHECKBOX_COLUMN_WIDTH,
            backgroundColor: 'transparent',
          }}
        >
          {!isNewRow && row.wipId != null && (
            <Box display="flex" justifyContent="flex-start" alignItems="center" height="100%">
              <Checkbox
                size="medium"
                color="primary"
                id={`wip-checkbox-${row.wipId}`}
                checked={isItemSelected}
                onClick={handleCheckboxClick}
                sx={{
                  width: CHECKBOX_SIZE,
                  height: CHECKBOX_SIZE,
                  '& .MuiSvgIcon-root': { fontSize: CHECKBOX_ICON_FONT_SIZE },
                  display: isHovered || isItemSelected ? 'block' : 'none',
                }}
                inputProps={{ 'aria-label': `Select WIP item ${row.repairOrderNumber ?? ''}` }}
              />
            </Box>
          )}
        </TableCell>
        {visibleColumns.map(
          (column): JSX.Element => (
            <TableCell
              key={column.id}
              align={column.alignment}
              sx={{
                minWidth: column.minWidth,
                width: column.width ?? column.minWidth,
                overflow: 'hidden',
                px: 1,
                py: 1,
              }}
            >
              {renderField(column, column === lastEditableColumn)}
            </TableCell>
          )
        )}
      </TableRow>
    </LocalizationProvider>
  );
};

export const WIPItemRow = memo(WIPItemRowComponent);
